import { jwtDecode } from 'jwt-decode';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getCookieCustom } from '../utils/cookies';

export type AuthUser = {
 jti: string;
 email: string;
 name: string;
 userId: string;
 databases: string;
 currentlyActiveDb: string;
 nbf: number;
 exp: number;
 iat: number;
 iss: string;
 aud: string;
};

export type AuthContextValue = {
 user: AuthUser | null;
 isAuthenticated: boolean;
 handleRefreshAuth: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const getDecodedUserFromToken = () => {
 const accessTokenCookie = getCookieCustom('access_token');
 if (!accessTokenCookie) {
  return null;
 }
 try {
  const decoded = jwtDecode(accessTokenCookie) as AuthUser;
  return decoded;
 } catch (error) {
  console.error('Failed to decode access token', error);
  return null;
 }
};

type AuthProviderProps = {
 children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
 const [user, setUser] = useState<AuthUser | null>(() => getDecodedUserFromToken());
 const location = useLocation();

 const handleRefreshAuth = useCallback(() => {
  const decodedUser = getDecodedUserFromToken();
  setUser(decodedUser);
 }, []);

 useEffect(() => {
  handleRefreshAuth();
 }, [handleRefreshAuth]);

 useEffect(() => {
  handleRefreshAuth();
 }, [location.key, handleRefreshAuth]);

 const value = useMemo(
  () => ({
   user,
   isAuthenticated: Boolean(user),
   handleRefreshAuth,
  }),
  [user, handleRefreshAuth],
 );

 return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
 const context = useContext(AuthContext);
 if (!context) {
  throw new Error('useAuthContext must be used within an AuthProvider');
 }
 return context;
};
